import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Car, Mail, ArrowLeft } from "lucide-react";

export const PrivacyPolicy = () => {
  return (
    <div className="privacy-page">
      <Helmet>
        <title>Privacy Policy | Moto Money Finance</title>
        <meta name="description" content="Read our Privacy Policy to understand how Moto Money Finance collects, uses, and protects your personal information." />
      </Helmet>

      {/* Header */}
      <header className="site-header">
        <div className="header-container">
          <Link to="/" className="logo">
            <Car size={32} />
            <span>Moto Money<strong>Finance</strong></span>
          </Link>
          <nav className="nav-links">
            <Link to="/" className="nav-link">
              <ArrowLeft size={18} />
              Back to Home
            </Link>
          </nav>
        </div>
      </header>

      <div className="privacy-container">
        <div className="privacy-content">
          <h1>Privacy Policy</h1>
          <p className="last-updated">Last Updated: December 2025</p>

          <section>
            <h2>1. Introduction</h2>
            <p>
              Moto Money Finance ("we," "our," or "us") is committed to protecting your privacy. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your 
              information when you visit our website motomoneyfinance.com or use our services.
            </p>
            <p>
              By using our website and services, you consent to the data practices described 
              in this policy. If you do not agree with the terms of this Privacy Policy, 
              please do not access or use our services.
            </p>
          </section>

          <section>
            <h2>2. Information We Collect</h2>
            <p>We collect information that you provide directly to us, including:</p>
            
            <h3>Financing Applications</h3>
            <ul>
              <li><strong>Personal Information:</strong> Name, email address, phone number, 
              mailing address, date of birth</li>
              <li><strong>Financial Information:</strong> Employment status, income information, 
              housing payment details, vehicle preferences, and budget requirements</li>
            </ul>

            <h3>Referral Submissions</h3>
            <ul>
              <li><strong>Your Information:</strong> Name, phone number, email address</li>
              <li><strong>Friend's Information:</strong> Name, phone number, email (if provided), 
              community, province, and vehicle interest</li>
              <li>We require confirmation that your friend has consented to being contacted before 
              submitting their information</li>
            </ul>

            <h3>Trade-In Enquiries</h3>
            <ul>
              <li><strong>Contact Information:</strong> Name, phone number, email, community, province</li>
              <li><strong>Vehicle Information:</strong> Year, make, model, mileage, condition, 
              and any balance owing on the vehicle</li>
              <li><strong>Preferences:</strong> What type of vehicle you're looking for</li>
            </ul>

            <p>
              We also automatically collect certain information when you visit our website, 
              including your IP address, browser type, operating system, referring URLs, 
              and information about how you interact with our website.
            </p>
          </section>

          <section>
            <h2>3. How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul>
              <li>Process and evaluate your financing application</li>
              <li>Contact you about your application, referral, or trade-in enquiry</li>
              <li>Connect you with appropriate financing options</li>
              <li>Follow up on referrals you submit (with the referred person's consent)</li>
              <li>Assess trade-in vehicles and discuss options</li>
              <li>Provide customer support and respond to your inquiries</li>
              <li>Improve our website and services</li>
              <li>Send you marketing communications (with your consent)</li>
              <li>Comply with legal obligations</li>
              <li>Protect against fraudulent or unauthorized activity</li>
            </ul>
          </section>

          <section>
            <h2>4. Information Sharing and Disclosure</h2>
            <p>We may share your information with:</p>
            <ul>
              <li><strong>Lending Partners:</strong> We share your application information 
              with our network of lenders and financial institutions to help you obtain 
              vehicle financing</li>
              <li><strong>Dealership Partners:</strong> We may share information with 
              authorized dealerships to facilitate your vehicle purchase</li>
              <li><strong>Service Providers:</strong> We may share information with 
              third-party vendors who assist us in operating our website and providing 
              our services</li>
              <li><strong>Legal Requirements:</strong> We may disclose information when 
              required by law or to protect our rights and safety</li>
            </ul>
            <p>
              <strong>We do not sell your personal information to third parties.</strong>
            </p>
          </section>

          <section>
            <h2>5. Referral Information</h2>
            <p>
              When you refer a friend to Moto Money Finance, you must confirm that your friend 
              has given you permission to share their contact information and has agreed to be 
              contacted by us. We will use your friend's information solely to reach out about 
              vehicle financing opportunities.
            </p>
            <p>
              We retain the referrer's information to track the source of referrals. If you 
              are referred by someone, we may share status updates with the referrer regarding 
              whether contact was made, but we will not share details of your application or 
              financial information with them.
            </p>
          </section>

          <section>
            <h2>6. Data Security</h2>
            <p>
              We implement appropriate technical and organizational security measures to 
              protect your personal information against unauthorized access, alteration, 
              disclosure, or destruction. These measures include:
            </p>
            <ul>
              <li>SSL/TLS encryption for data transmission</li>
              <li>Secure servers and data storage</li>
              <li>Access controls and authentication procedures</li>
              <li>Regular security assessments</li>
            </ul>
            <p>
              However, no method of transmission over the Internet or electronic storage 
              is 100% secure. While we strive to protect your information, we cannot 
              guarantee absolute security.
            </p>
          </section>

          <section>
            <h2>7. Your Rights and Choices</h2>
            <p>You have the right to:</p>
            <ul>
              <li><strong>Access:</strong> Request access to the personal information we 
              hold about you</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or 
              incomplete information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information, 
              subject to certain exceptions</li>
              <li><strong>Opt-Out:</strong> Opt out of receiving marketing communications 
              from us</li>
              <li><strong>Withdraw Consent:</strong> Withdraw your consent to our 
              processing of your information</li>
            </ul>
            <p>
              To exercise any of these rights, please contact us at 
              sales@motomoneyfinance.com.
            </p>
          </section>

          <section>
            <h2>8. Cookies and Tracking Technologies</h2>
            <p>
              We use cookies and similar tracking technologies to collect information about 
              your browsing activities. Cookies are small data files stored on your device 
              that help us improve our website and your experience.
            </p>
            <p>You can control cookies through your browser settings. However, disabling 
            cookies may affect the functionality of our website.</p>
          </section>

          <section>
            <h2>9. Third-Party Links</h2>
            <p>
              Our website may contain links to third-party websites. We are not responsible 
              for the privacy practices or content of these external sites. We encourage 
              you to review the privacy policies of any third-party websites you visit.
            </p>
          </section>

          <section>
            <h2>10. Children's Privacy</h2>
            <p>
              Our services are not intended for individuals under the age of 18. We do not 
              knowingly collect personal information from children. If we become aware that 
              we have collected information from a child, we will take steps to delete it.
            </p>
          </section>

          <section>
            <h2>11. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of 
              any material changes by posting the new Privacy Policy on this page and 
              updating the "Last Updated" date. Your continued use of our services after 
              any changes constitutes your acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2>12. Canadian Privacy Laws</h2>
            <p>
              We comply with applicable Canadian privacy laws, including the Personal 
              Information Protection and Electronic Documents Act (PIPEDA) and applicable 
              provincial privacy legislation. We are committed to handling your personal 
              information in accordance with these laws.
            </p>
          </section>

          <section>
            <h2>13. Contact Us</h2>
            <p>
              If you have any questions or concerns about this Privacy Policy or our data 
              practices, please contact us at:
            </p>
            <div className="contact-info">
              <p><strong>Moto Money Finance</strong></p>
              <p>
                <Mail size={16} style={{ display: 'inline', marginRight: '8px' }} />
                Email: <a href="mailto:sales@motomoneyfinance.com">sales@motomoneyfinance.com</a>
              </p>
            </div>
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer className="site-footer" style={{ marginTop: '0' }}>
        <div className="footer-bottom">
          <p>&copy; {new Date().getFullYear()} Moto Money Finance. All rights reserved.</p>
          <p>
            <Link to="/privacy">Privacy Policy</Link>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
